import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import { CollectionGroupModel } from '../../context/models/collections/CollectionGroupModel';
import CollectionGroupDetail from './CollectionGroupDetail';
import { CollectionGroupActionTypes } from '../../context/stores/collections-group/ICollectionGroupActionsTypes';
import { useLookupCollectionType } from '../../components/shared/hoc-collection-type/useLookupCollectionType';
import ComboBoxCollectionType from '../../components/shared/hoc-collection-type/ComboBoxCollectionType';
import { CollectionGroupResponse } from '../../context/models/collections/CollectionGroupResponse';
import { useNavigate } from 'react-router-dom';
// import ArticleCategoryDetail from './ArticleCategoryDetail';
type ICollectionGroupPageProps = {
	titlePage: string;
	current_type_id: number;
	status: eBaseActionStatus;
	allCollectionsGroup: CollectionGroupResponse[];
	selectedIds: number[];
	showDetail: BaseShowDetail<CollectionGroupModel>;
	showConfirm: boolean;
	handleReloadAllData: (type_id: number) => void;
	handleSaveData: (data: CollectionGroupModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<CollectionGroupModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `vị trí hiển thị`,
		status: state.collectionGroup.status,
		current_type_id: parseInt(localStorage.getItem('current_type_id') || '1'),
		allCollectionsGroup: state.collectionGroup.allCollectionsGroup,
		selectedIds: state.collectionGroup.selectedIds,
		showDetail: state.collectionGroup.showDetail,
		showConfirm: state.collectionGroup.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<CollectionGroupActionTypes>) => ({
	handleReloadAllData: (typeID: number) => {
		dispatch(actions.collectionGroup.getAllRequest({ typeID }));
	},
	handleSaveData: (data: CollectionGroupModel) => {
		dispatch(actions.collectionGroup.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.collectionGroup.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.collectionGroup.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<CollectionGroupModel>) => {
		dispatch(actions.collectionGroup.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.collectionGroup.showConfirm(isShow));
	},
});

const CollectionGroupPage = (props: ICollectionGroupPageProps) => {
	const navigate = useNavigate();
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allCollectionsGroup,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
		current_type_id,
	} = props;
	allCollectionsGroup.sort((a, b) => a.stt - b.stt);
	const [typeID, setTypeID] = useState<number>(current_type_id);

	const { collection_type_all } = useLookupCollectionType();

	var handleReloadData = useCallback(() => {
		handleReloadAllData(typeID);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [typeID]);

	useEffect(() => {
		handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [typeID]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<CollectionGroupResponse>[] => {
		return [
			{ accessorKey: 'stt', header: 'Số thứ tự', size: 100, meta: { align: 'center' } },
			{
				accessorKey: 'type',
				header: 'Vị trí',
				size: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const type = collection_type_all.find((g: any) => g.id === val);
					return <span className='font-medium'>{type ? type.name : val}</span>;
				}
			},
			{ accessorKey: 'name', header: 'Tên nhóm', minSize: 150 },
			{ accessorKey: 'title', header: 'Tiêu đề', minSize: 150 },
			{ accessorKey: 'sapo', header: 'Sapo', size: 150 },
			{ accessorKey: 'icon', header: 'Icon', size: 150 },
			{ accessorKey: 'image', header: 'Ảnh', size: 150 },
			{
				id: 'actions',
				header: 'Thao tác',
				meta: { align: 'center' },
				size: 120,
				cell: ({ row }) => {
					return row.original.id > 0 ? (
						<div className="flex gap-2 justify-center">
							<Button
								onClick={() => {
									localStorage.setItem('current_group_id', String(row.original.id));
									navigate(`/collections`, { replace: true });
								}}
								text=''
								icon='ni ni-grid-box-alt-fill'
								preset='dimoutline'
								theme='info'
								title='Bộ sưu tập thuộc nhóm'
							/>
							<Button
								onClick={() => onShowDetail({ isShow: true, detailData: row.original })}
								text=''
								icon='ni ni-edit'
								preset='dimoutline'
								theme='primary'
								title='Sửa'
							/>
						</div>
					) : null;
				}
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collection_type_all, navigate, onShowDetail]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							{selectedIds.length > 0 && (
								<li>
									<Button
										text={`Xóa (${selectedIds.length})`}
										icon='icon ni ni-trash'
										className='hidden md:inline-flex'
										theme='danger'
										onClick={() => onShowConfirm(true)}
									/>
								</li>
							)}

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allCollectionsGroup}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allCollectionsGroup.length}
								enableGlobalFilter={false}
								toolbar={{
									isShowSearch: false,
									extraActions: (
										<div className="w-[300px]">
											<ComboBoxCollectionType
												name={'typeID'}
												onValueChanged={(value) => {
													setTypeID(value ? parseInt(value) : 0);
												}}
												value={typeID}
												placeholder='Nhập vị trí...'
												sizeClass='sm'
											/>
										</div>
									)
								}}
								onRefresh={handleReloadData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<CollectionGroupDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(CollectionGroupPage);
